import { useEffect, useRef, useState, type ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useLocation } from "wouter";
import { Capacitor } from "@capacitor/core";
import { FirebaseAuthentication } from "@capacitor-firebase/authentication";
import Header from "@/components/Header";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { Loader2, Upload, ChevronRight, ShoppingBag, Heart, Truck, Store, Share2, Shield, LogOut, Pencil, User, Gift, Sprout, Bell, X, FileText, Ban, Flag, Check } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { recordingModeActive, setRecordingMode } from "@/lib/recordingMode";
import { useHideGoogleOnIos, useAppleSignInAvailable } from "@/hooks/useHideGoogleOnIos";
import { AppleIcon } from "@/components/OAuthButtons";

const DELETE_REASONS = [
  "Not enough sellers or produce near me",
  "Too expensive / fees too high",
  "The app was confusing or buggy",
  "I found what I needed elsewhere",
  "Just exploring, don't need it right now",
  "Privacy concerns",
  "Other",
];

type AccountUser = {
  id: string;
  email: string | null;
  firstName: string | null;
  lastName: string | null;
  displayName?: string | null;
  bio?: string | null;
  profileImageUrl?: string | null;
  coverImageUrl?: string | null;
  sellerType?: string | null;
  addressLine1?: string | null;
  addressLine2?: string | null;
  city?: string | null;
  state?: string | null;
  roles?: string | null;
  zipCode?: string | null;
  glutenFreeMaker?: number | null;
  glutenFreeAllProducts?: number | null;
  canBuy?: boolean;
  canSell?: boolean;
};

type FormState = {
  firstName: string;
  lastName: string;
  displayName: string;
  bio: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  zipCode: string;
  roles: string;
  sellerType: string;
  profileImageUrl: string;
  coverImageUrl: string;
};

function AccountMenuItem({ icon: Icon, label, href, onClick, badge }: { icon: typeof ShoppingBag; label: string; href?: string; onClick?: () => void; badge?: string }) {
  const content = (
    <div className="flex items-center gap-3 px-4 py-3">
      <Icon className="h-5 w-5 text-muted-foreground" />
      <span className="flex-1 text-sm font-medium">{label}</span>
      {badge && <span className="text-xs text-muted-foreground">{badge}</span>}
      <ChevronRight className="h-4 w-4 text-muted-foreground" />
    </div>
  );
  if (href) return <Link href={href} className="block">{content}</Link>;
  if (onClick) return <button type="button" onClick={onClick} className="block w-full text-left">{content}</button>;
  return content;
}

const FIELD_BASE =
  "peer w-full rounded-xl border border-border/50 bg-background/40 px-4 text-base text-foreground transition-colors placeholder:text-transparent focus:border-primary focus:bg-background focus:outline-none focus:ring-1 focus:ring-primary/30";
const FIELD_LABEL =
  "pointer-events-none absolute left-4 top-4 text-base text-muted-foreground transition-all duration-150 peer-focus:top-1.5 peer-focus:text-xs peer-focus:font-medium peer-focus:text-primary peer-[:not(:placeholder-shown)]:top-1.5 peer-[:not(:placeholder-shown)]:text-xs peer-[:not(:placeholder-shown)]:font-medium";

function FloatingField({
  label,
  value,
  onChange,
  type = "text",
  textarea = false,
  rows,
  maxLength,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: string;
  textarea?: boolean;
  rows?: number;
  maxLength?: number;
}) {
  return (
    <div className="relative">
      {textarea ? (
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          rows={rows}
          maxLength={maxLength}
          placeholder=" "
          className={cn(FIELD_BASE, "min-h-[120px] pt-7 pb-2 leading-relaxed")}
        />
      ) : (
        <input
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          maxLength={maxLength}
          placeholder=" "
          className={cn(FIELD_BASE, "h-14 pt-5 pb-1")}
        />
      )}
      <label className={FIELD_LABEL}>{label}</label>
    </div>
  );
}

function FloatingSelect({
  label,
  value,
  onChange,
  children,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  children: ReactNode;
}) {
  return (
    <div className="relative">
      <label className="pointer-events-none absolute left-4 top-1.5 text-xs font-medium text-muted-foreground">
        {label}
      </label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-14 w-full rounded-xl border border-border/50 bg-background/40 px-4 pt-5 pb-1 text-base text-foreground transition-colors focus:border-primary focus:bg-background focus:outline-none focus:ring-1 focus:ring-primary/30"
      >
        {children}
      </select>
    </div>
  );
}

const CROP_VIEWPORT = 256;
const CROP_OUTPUT = 512;

function ProfileImageCropper({
  src,
  busy,
  onCancel,
  onConfirm,
}: {
  src: string;
  busy: boolean;
  onCancel: () => void;
  onConfirm: (blob: Blob) => void;
}) {
  const imgRef = useRef<HTMLImageElement | null>(null);
  const [nat, setNat] = useState<{ w: number; h: number } | null>(null);
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const drag = useRef<{ x: number; y: number } | null>(null);

  // base "cover" scale so the image fills the viewport at scale=1
  const coverScale = nat ? Math.max(CROP_VIEWPORT / nat.w, CROP_VIEWPORT / nat.h) : 1;
  const dispW = nat ? nat.w * coverScale * scale : CROP_VIEWPORT;
  const dispH = nat ? nat.h * coverScale * scale : CROP_VIEWPORT;

  const clamp = (x: number, y: number) => {
    const maxX = Math.max(0, (dispW - CROP_VIEWPORT) / 2);
    const maxY = Math.max(0, (dispH - CROP_VIEWPORT) / 2);
    return {
      x: Math.min(maxX, Math.max(-maxX, x)),
      y: Math.min(maxY, Math.max(-maxY, y)),
    };
  };

  useEffect(() => {
    setOffset((o) => clamp(o.x, o.y));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scale, nat]);

  const onPointerDown = (e: React.PointerEvent) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    drag.current = { x: e.clientX, y: e.clientY };
  };
  const onPointerMove = (e: React.PointerEvent) => {
    if (!drag.current) return;
    const dx = e.clientX - drag.current.x;
    const dy = e.clientY - drag.current.y;
    drag.current = { x: e.clientX, y: e.clientY };
    setOffset((o) => clamp(o.x + dx, o.y + dy));
  };
  const onPointerUp = () => {
    drag.current = null;
  };

  const handleConfirm = () => {
    const img = imgRef.current;
    if (!img || !nat) return;
    const canvas = document.createElement("canvas");
    canvas.width = CROP_OUTPUT;
    canvas.height = CROP_OUTPUT;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const ratio = CROP_OUTPUT / CROP_VIEWPORT;
    const left = (CROP_VIEWPORT / 2 + offset.x - dispW / 2) * ratio;
    const top = (CROP_VIEWPORT / 2 + offset.y - dispH / 2) * ratio;
    ctx.drawImage(img, left, top, dispW * ratio, dispH * ratio);
    canvas.toBlob(
      (blob) => {
        if (blob) onConfirm(blob);
      },
      "image/jpeg",
      0.9,
    );
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 p-4">
      <div className="w-full max-w-sm rounded-xl bg-background p-4 shadow-lg">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-sm font-semibold">Adjust profile photo</h2>
          <button type="button" onClick={onCancel} disabled={busy} className="text-muted-foreground">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex justify-center">
          <div
            className="relative overflow-hidden rounded-full border bg-muted touch-none"
            style={{ width: CROP_VIEWPORT, height: CROP_VIEWPORT }}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
          >
            <img
              ref={imgRef}
              src={src}
              alt="Crop preview"
              draggable={false}
              onLoad={(e) => setNat({ w: e.currentTarget.naturalWidth, h: e.currentTarget.naturalHeight })}
              className="absolute select-none"
              style={{
                width: dispW,
                height: dispH,
                left: CROP_VIEWPORT / 2 + offset.x - dispW / 2,
                top: CROP_VIEWPORT / 2 + offset.y - dispH / 2,
                maxWidth: "none",
              }}
            />
          </div>
        </div>

        <div className="mt-4 flex items-center gap-3">
          <span className="text-xs text-muted-foreground">Zoom</span>
          <input
            type="range"
            min={1}
            max={3}
            step={0.01}
            value={scale}
            onChange={(e) => setScale(Number(e.target.value))}
            className="flex-1"
          />
        </div>
        <p className="mt-1 text-xs text-muted-foreground">Drag to reposition, slide to zoom.</p>

        <div className="mt-4 flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onCancel} disabled={busy}>
            Cancel
          </Button>
          <Button type="button" onClick={handleConfirm} disabled={busy || !nat}>
            {busy ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Uploading
              </>
            ) : (
              "Use photo"
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function Account() {
  const queryClient = useQueryClient();

  // The auth-user cache gets overwritten by partial objects from the
  // login/register/onboarding setQueryData calls (which omit hasPassword), and
  // staleTime: Infinity keeps them — so hasPassword can be stale-undefined here,
  // hiding the delete-flow password field and making self-delete fail for
  // password accounts. Refetch the authoritative record on mount (trap #1).
  useEffect(() => {
    void queryClient.refetchQueries({ queryKey: ["/api/auth/user"] });
  }, [queryClient]);
  const { user } = useAuth();
  const [, setLocation] = useLocation();
  const [view, setView] = useState<"overview" | "edit" | "security">(() =>
    new URLSearchParams(window.location.search).has("edit") ? "edit" : "overview",
  );
  const profileImageInputRef = useRef<HTMLInputElement | null>(null);
  const coverImageInputRef = useRef<HTMLInputElement | null>(null);

  const [form, setForm] = useState<FormState>({
    firstName: "",
    lastName: "",
    displayName: "",
    bio: "",
    addressLine1: "",
    addressLine2: "",
    city: "",
    state: "",
    zipCode: "",
    roles: "",
    sellerType: "",
    profileImageUrl: "",
    coverImageUrl: "",
  });
  const [isSaving, setIsSaving] = useState(false);
  const [glutenFreeMaker, setGlutenFreeMaker] = useState(false);
  const [glutenFreeAllProducts, setGlutenFreeAllProducts] = useState(false);
  const [isUploadingProfileImage, setIsUploadingProfileImage] = useState(false);
  const [isUploadingCoverImage, setIsUploadingCoverImage] = useState(false);
  const [profileCropSrc, setProfileCropSrc] = useState<string | null>(null);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);

  const hasPassword = Boolean((user as any)?.hasPassword);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteReason, setDeleteReason] = useState("");
  const [deleteDetail, setDeleteDetail] = useState("");
  const [deletePassword, setDeletePassword] = useState("");
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deleteBlockers, setDeleteBlockers] = useState<string[]>([]);

  const handleDeleteAccount = async () => {
    setDeleteError(null);
    setDeleteBlockers([]);
    if (deleteConfirmText.trim().toUpperCase() !== "DELETE") {
      setDeleteError("Type DELETE to confirm.");
      return;
    }
    if (hasPassword && !deletePassword) {
      setDeleteError("Enter your password to confirm.");
      return;
    }
    setIsDeleting(true);
    try {
      const res = await fetch("/api/account/delete", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          password: deletePassword || undefined,
          confirm: true,
          reason: deleteReason || undefined,
          reasonDetail: deleteDetail || undefined,
        }),
      });
      if (res.status === 409) {
        const data = await res.json().catch(() => ({}));
        setDeleteBlockers(Array.isArray(data.blockers) ? data.blockers : []);
        setDeleteError(data.message || "Finish up before deleting your account.");
        return;
      }
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || "Failed to delete account");
      }
      queryClient.clear();
      window.location.replace("/");
    } catch (err: any) {
      setDeleteError(err?.message || "Failed to delete account");
    } finally {
      setIsDeleting(false);
    }
  };

  const { data, isLoading, error } = useQuery<AccountUser>({
    queryKey: ["/api/account"],
    refetchOnWindowFocus: false,
    queryFn: async () => {
      const res = await fetch("/api/account", { credentials: "include" });
      if (!res.ok) throw new Error(await res.text());
      return res.json();
    },
  });

  // Pre-load the share URL so the "Share & Invite" handler can call
  // navigator.share() synchronously on click. Awaiting a fetch inside the
  // click handler risks the gesture's transient activation expiring before
  // the share sheet tries to open, which presents as "the button does
  // nothing" — the symptom users describe as the share link not working.
  const { data: referralCodeData } = useQuery<{ code: string; shareUrl: string }>({
    queryKey: ["/api/my/referral-code"],
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (!data) return;
    setForm({
      firstName: data.firstName ?? "",
      lastName: data.lastName ?? "",
      displayName: data.displayName ?? "",
      bio: data.bio ?? "",
      addressLine1: data.addressLine1 ?? "",
      addressLine2: data.addressLine2 ?? "",
      city: data.city ?? "",
      state: data.state ?? "",
      zipCode: data.zipCode ?? "",
      roles: data.roles ?? "",
      sellerType: data.sellerType ?? "",
      profileImageUrl: data.profileImageUrl ?? "",
      coverImageUrl: data.coverImageUrl ?? "",
    });
    setGlutenFreeMaker(Number(data.glutenFreeMaker) === 1);
    setGlutenFreeAllProducts(Number(data.glutenFreeAllProducts) === 1);
  }, [data]);

  const setField = (key: keyof FormState, value: string) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const showSellerType = form.roles === "seller" || form.roles === "both";

  const uploadImage = async (
    endpoint: "/api/uploads/profile-image" | "/api/uploads/cover-image",
    file: File,
    field: "profileImageUrl" | "coverImageUrl",
    setUploading: (value: boolean) => void,
  ) => {
    setUploading(true);
    setSaveError(null);
    setSaveMessage(null);

    try {
      const formData = new FormData();
      formData.append("image", file);

      const res = await fetch(endpoint, {
        method: "POST",
        credentials: "include",
        body: formData,
      });

      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || "Image upload failed");
      }

      const payload = await res.json();
      setField(field, payload.imageUrl ?? "");
      setSaveMessage("Image uploaded. Save profile to publish it.");
    } catch (err: any) {
      setSaveError(err?.message || "Image upload failed");
    } finally {
      setUploading(false);
    }
  };

  const handleProfileImageSelected = (file: File | null) => {
    if (!file) return;
    setSaveError(null);
    setSaveMessage(null);
    setProfileCropSrc(URL.createObjectURL(file));
  };

  const handleAdjustProfileImage = async () => {
    const url = form.profileImageUrl.trim();
    if (!url) return;
    setSaveError(null);
    setSaveMessage(null);
    try {
      const res = await fetch(url, { mode: "cors" });
      if (!res.ok) throw new Error("fetch failed");
      const blob = await res.blob();
      setProfileCropSrc(URL.createObjectURL(blob));
    } catch {
      setSaveError("Couldn't load the current photo to adjust. Try re-uploading it.");
    }
  };

  const handleProfileCropConfirm = async (blob: Blob) => {
    const file = new File([blob], "profile.jpg", { type: "image/jpeg" });
    await uploadImage("/api/uploads/profile-image", file, "profileImageUrl", setIsUploadingProfileImage);
    if (profileCropSrc) URL.revokeObjectURL(profileCropSrc);
    setProfileCropSrc(null);
  };

  const handleProfileCropCancel = () => {
    if (profileCropSrc) URL.revokeObjectURL(profileCropSrc);
    setProfileCropSrc(null);
  };

  const handleCoverImageSelected = async (file: File | null) => {
    if (!file) return;
    await uploadImage("/api/uploads/cover-image", file, "coverImageUrl", setIsUploadingCoverImage);
  };

  const handleSave = async () => {
    setIsSaving(true);
    setSaveMessage(null);
    setSaveError(null);

    try {
      const payload = {
        firstName: form.firstName.trim() || undefined,
        lastName: form.lastName.trim() || undefined,
        displayName: form.displayName.trim() || undefined,
        bio: form.bio.trim() || undefined,
        addressLine1: form.addressLine1.trim() || undefined,
        addressLine2: form.addressLine2.trim() || undefined,
        city: form.city.trim() || undefined,
        state: form.state.trim() || undefined,
        zipCode: form.zipCode.trim() || undefined,
        roles: form.roles || undefined,
        sellerType: showSellerType ? form.sellerType || undefined : undefined,
        glutenFreeMaker: showSellerType ? (glutenFreeMaker ? 1 : 0) : undefined,
        glutenFreeAllProducts: showSellerType
          ? (glutenFreeMaker && glutenFreeAllProducts ? 1 : 0)
          : undefined,
        profileImageUrl: form.profileImageUrl.trim() || undefined,
        coverImageUrl: form.coverImageUrl.trim() || undefined,
      };

      const res = await fetch("/api/account/profile", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || "Failed to save account profile");
      }

      const updated = await res.json();
      const { geocodeStatus, ...userOnly } = updated as { geocodeStatus?: string } & Record<string, unknown>;
      queryClient.setQueryData(["/api/account"], userOnly);
      queryClient.setQueryData(["/api/auth/user"], userOnly);
      if (geocodeStatus === "not_found") {
        setSaveMessage(
          "Profile saved, but we couldn't locate that address on the map. Double-check the street, city, state, and zip — buyers won't see you in nearby search until it's locatable."
        );
      } else if (geocodeStatus === "error") {
        setSaveMessage(
          "Profile saved, but the address lookup failed (try saving again in a minute). You won't appear in nearby search until it succeeds."
        );
      } else {
        setSaveMessage("Profile saved");
      }
    } catch (err: any) {
      setSaveError(err?.message || "Failed to save account profile");
    } finally {
      setIsSaving(false);
    }
  };

  const [newEmail, setNewEmail] = useState("");
  const [confirmEmail, setConfirmEmail] = useState("");
  const [emailPassword, setEmailPassword] = useState("");
  const [isChangingEmail, setIsChangingEmail] = useState(false);
  const [emailMessage, setEmailMessage] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);

  const handleEmailChange = async () => {
    setIsChangingEmail(true);
    setEmailMessage(null);
    setEmailError(null);

    try {
      const res = await fetch("/api/account/email", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          currentPassword: emailPassword,
          newEmail,
          confirmEmail,
        }),
      });

      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || "Failed to update email");
      }

      const updated = await res.json();
      queryClient.setQueryData(["/api/account"], updated);
      queryClient.setQueryData(["/api/auth/user"], updated);
      setEmailPassword("");
      setNewEmail("");
      setConfirmEmail("");
      setEmailMessage("Email updated");
    } catch (err: any) {
      setEmailError(err?.message || "Failed to update email");
    } finally {
      setIsChangingEmail(false);
    }
  };

  const handlePasswordChange = async () => {
    setIsChangingPassword(true);
    setPasswordMessage(null);
    setPasswordError(null);

    try {
      const res = await fetch("/api/account/password", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          currentPassword,
          newPassword,
          confirmPassword,
        }),
      });

      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || "Failed to update password");
      }

      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setPasswordMessage("Password updated");
    } catch (err: any) {
      setPasswordError(err?.message || "Failed to update password");
    } finally {
      setIsChangingPassword(false);
    }
  };

  const hideGoogle = useHideGoogleOnIos();
  const [isLinkingGoogle, setIsLinkingGoogle] = useState(false);
  const [linkGoogleMessage, setLinkGoogleMessage] = useState<string | null>(null);
  const [linkGoogleError, setLinkGoogleError] = useState<string | null>(null);

  const handleLinkGoogle = async () => {
    setIsLinkingGoogle(true);
    setLinkGoogleMessage(null);
    setLinkGoogleError(null);
    try {
      if (!Capacitor.isNativePlatform()) {
        throw new Error("Linking Google requires the Stoop iOS or Android app.");
      }
      const result = await FirebaseAuthentication.signInWithGoogle();
      const idToken = (result as any)?.credential?.idToken;
      if (!idToken) throw new Error("Google sign-in was cancelled.");
      const res = await fetch("/api/auth/google/link", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ idToken }),
      });
      if (!res.ok) {
        const text = await res.text();
        let message = "Couldn't link Google.";
        try {
          message = JSON.parse(text).message || message;
        } catch {
          if (text) message = text;
        }
        throw new Error(message);
      }
      setLinkGoogleMessage("Google linked. You can now sign in with Google next time.");
      await queryClient.invalidateQueries({ queryKey: ["/api/auth/user"] });
    } catch (err: any) {
      setLinkGoogleError(err?.message || "Couldn't link Google.");
    } finally {
      setIsLinkingGoogle(false);
    }
  };

  const appleAvailable = useAppleSignInAvailable();
  const appleLinked = Boolean(user?.linkedProviders?.includes("apple"));
  const [isLinkingApple, setIsLinkingApple] = useState(false);
  const [linkAppleMessage, setLinkAppleMessage] = useState<string | null>(null);
  const [linkAppleError, setLinkAppleError] = useState<string | null>(null);

  const handleLinkApple = async () => {
    setIsLinkingApple(true);
    setLinkAppleMessage(null);
    setLinkAppleError(null);
    try {
      const result = await FirebaseAuthentication.signInWithApple();
      const idToken = (result as any)?.credential?.idToken;
      if (!idToken) throw new Error("Apple sign-in was cancelled.");
      const res = await fetch("/api/auth/apple/link", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ idToken }),
      });
      if (!res.ok) {
        const text = await res.text();
        let message = "Couldn't link Apple.";
        try {
          message = JSON.parse(text).message || message;
        } catch {
          if (text) message = text;
        }
        throw new Error(message);
      }
      setLinkAppleMessage("Apple linked. Next time just tap Sign in with Apple.");
      await queryClient.invalidateQueries({ queryKey: ["/api/auth/user"] });
    } catch (err: any) {
      const message = String(err?.message || err);
      if (/cancel|1001/i.test(message)) {
        setLinkAppleError(null);
      } else {
        setLinkAppleError(message || "Couldn't link Apple.");
      }
    } finally {
      setIsLinkingApple(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <Header />
        <main className="flex-1">
          <div className="container mx-auto max-w-4xl px-4 py-10">
            <div className="flex items-center gap-2 text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading account…
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <Header />
        <main className="flex-1">
          <div className="container mx-auto max-w-4xl px-4 py-10">
            <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">
              Unable to load your account right now.
            </div>
          </div>
        </main>
      </div>
    );
  }

  const handleLogout = async () => {
    try {
      await fetch("/api/logout", { method: "POST", credentials: "include" });
      window.localStorage.removeItem("marketlink.mode");
      window.location.assign("/login");
    } catch { /* ignore */ }
  };

  const displayName = data?.displayName || [data?.firstName, data?.lastName].filter(Boolean).join(" ") || "User";
  const roleBadge = data?.roles === "both" ? "Buyer & Seller" : data?.roles === "seller" ? "Seller" : "Buyer";
  const canSell = data?.canSell || data?.roles === "seller" || data?.roles === "both";

  if (view === "overview") {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <Header />
        <main className="flex-1">
          <div className="mx-auto max-w-lg">
            {/* Profile header */}
            <div className="relative">
              {data?.coverImageUrl ? (
                <img src={data.coverImageUrl} alt="Cover" className="h-28 w-full object-cover" />
              ) : (
                <div className="h-28 w-full bg-brand-gradient-secondary" />
              )}
              <div className="absolute -bottom-10 left-4">
                <div className="h-20 w-20 overflow-hidden rounded-full border-4 border-background bg-muted shadow">
                  {data?.profileImageUrl ? (
                    <img src={data.profileImageUrl} alt={displayName} className="h-full w-full object-cover" />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center text-2xl font-bold text-muted-foreground">
                      {displayName.slice(0, 1).toUpperCase()}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="px-4 pt-12 pb-4">
              <h1 className="text-xl font-bold">{displayName}</h1>
              <p className="text-sm text-muted-foreground">{data?.email}</p>
              <span className="mt-1 inline-flex items-center rounded-full bg-primary/10 px-2.5 py-0.5 text-[11px] font-medium text-primary">
                {roleBadge}
              </span>
              {data?.bio && <p className="mt-2 text-sm text-muted-foreground">{data.bio}</p>}
            </div>

            {/* Navigation sections */}
            <div className="divide-y border-t">
              <AccountMenuItem icon={Pencil} label="Edit Profile" onClick={() => setView("edit")} />
              <AccountMenuItem icon={ShoppingBag} label="My Orders" href="/orders" />
              <AccountMenuItem icon={Sprout} label="Reservations" href="/reservations" />
              <AccountMenuItem icon={Heart} label="Saved Items" href="/?browse=saved" />
              {canSell && <AccountMenuItem icon={Store} label="Seller Dashboard" href="/seller" />}
              {canSell && <AccountMenuItem icon={Sprout} label="Growth Photos" href="/seller/growth-photos" />}
              <AccountMenuItem icon={Truck} label="Deliveries" href="/deliveries" />
              <AccountMenuItem icon={Bell} label="Notification Settings" href="/account/notifications" />
              <AccountMenuItem icon={Gift} label="Rewards & Referrals" href="/account/rewards" badge={(user as any)?.referralCode || undefined} />
              <AccountMenuItem icon={Share2} label="Share & Invite" onClick={async () => {
                const fallbackCode = (user as any)?.referralCode as string | undefined;
                const shareUrl =
                  referralCodeData?.shareUrl ??
                  (fallbackCode ? `${window.location.origin}/install?ref=${fallbackCode}` : null);
                if (!shareUrl) {
                  alert("Still loading your invite link — try again in a sec.");
                  return;
                }
                try {
                  if (navigator.share) {
                    await navigator.share({ title: "Stoop", text: "Join me on Stoop!", url: shareUrl });
                  } else {
                    await navigator.clipboard.writeText(shareUrl);
                    alert("Link copied!");
                  }
                } catch { /* cancelled */ }
              }} />
              <AccountMenuItem icon={Shield} label="Security & Account Deletion" onClick={() => setView("security")} />
              <AccountMenuItem icon={Ban} label="Blocked Accounts" href="/account/blocked" />
              {(user as any)?.isDemoAdmin && !recordingModeActive() && (
                <AccountMenuItem icon={Flag} label="Moderation Queue" href="/admin/reports" />
              )}
              {(user as any)?.isDemoAdmin && (
                <AccountMenuItem
                  icon={Shield}
                  label={recordingModeActive() ? "Recording mode: ON — tap to show admin tools" : "Recording mode: hide admin tools"}
                  onClick={() => setRecordingMode(!recordingModeActive())}
                />
              )}
              <AccountMenuItem icon={FileText} label="Terms of Use" href="/terms-of-use" />
              {canSell && <AccountMenuItem icon={FileText} label="Founding Maker Terms" href="/terms" />}
              <AccountMenuItem icon={Shield} label="Privacy Policy" href="/privacy" />
              <AccountMenuItem icon={LogOut} label="Log Out" onClick={handleLogout} />
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />

      <main className="flex-1">
        <div className="container mx-auto max-w-4xl px-4 py-8">
          <div className="mb-4">
            <Button variant="ghost" size="sm" onClick={() => setView("overview")} className="gap-1">
              ← Back
            </Button>
            <h1 className="mt-2 text-xl font-bold">{view === "security" ? "Security" : "Edit Profile"}</h1>
          </div>

          {view === "edit" && (
          <Card>
            <CardContent className="space-y-6 pt-6">
              <div className="grid gap-5 sm:grid-cols-2">
                <FloatingField
                  label="First name"
                  value={form.firstName}
                  onChange={(v) => setField("firstName", v)}
                />

                <FloatingField
                  label="Last name"
                  value={form.lastName}
                  onChange={(v) => setField("lastName", v)}
                />

                <div className="sm:col-span-2">
                  <FloatingField
                    label="Display name"
                    value={form.displayName}
                    onChange={(v) => setField("displayName", v)}
                  />
                </div>

                <div className="sm:col-span-2">
                  <FloatingField
                    label="Address line 1"
                    value={form.addressLine1}
                    onChange={(v) => setField("addressLine1", v)}
                    maxLength={200}
                  />
                </div>

                <div className="sm:col-span-2">
                  <FloatingField
                    label="Address line 2 (optional)"
                    value={form.addressLine2}
                    onChange={(v) => setField("addressLine2", v)}
                    maxLength={200}
                  />
                </div>

                <FloatingField
                  label="City"
                  value={form.city}
                  onChange={(v) => setField("city", v)}
                  maxLength={100}
                />

                <FloatingField
                  label="State"
                  value={form.state}
                  onChange={(v) => setField("state", v)}
                  maxLength={50}
                />

                <div className="sm:col-span-2">
                  <FloatingField
                    label="ZIP code"
                    value={form.zipCode}
                    onChange={(v) => setField("zipCode", v)}
                    maxLength={10}
                  />
                </div>

                <div className="sm:col-span-2">
                  <FloatingSelect
                    label="Role"
                    value={form.roles}
                    onChange={(v) => setField("roles", v)}
                  >
                    <option value="">Select role</option>
                    <option value="buyer">Buyer</option>
                    <option value="seller">Grower / Maker</option>
                    <option value="both">Buyer + Grower / Maker</option>
                  </FloatingSelect>
                </div>

                {showSellerType && (
                  <div className="sm:col-span-2">
                    <FloatingSelect
                      label="Producer type"
                      value={form.sellerType}
                      onChange={(v) => setField("sellerType", v)}
                    >
                      <option value="">Select producer type</option>
                      <option value="grower">Grower</option>
                      <option value="maker">Maker</option>
                      <option value="farmstand">Farm stand</option>
                      <option value="mixed">Mixed</option>
                    </FloatingSelect>
                  </div>
                )}

                {showSellerType && (
                  <div className="sm:col-span-2 rounded-lg border p-3">
                    <label className="flex items-center justify-between gap-3">
                      <span>
                        <span className="block text-sm font-medium">I make gluten-free products</span>
                        <span className="block text-xs text-muted-foreground">
                          Adds a gluten-free badge to your listings so buyers can spot them.
                        </span>
                      </span>
                      <input
                        type="checkbox"
                        className="h-5 w-5 accent-primary"
                        checked={glutenFreeMaker}
                        onChange={(e) => setGlutenFreeMaker(e.target.checked)}
                      />
                    </label>
                    {glutenFreeMaker && (
                      <div className="mt-3 grid grid-cols-2 gap-1.5">
                        <button
                          type="button"
                          onClick={() => setGlutenFreeAllProducts(true)}
                          className={`rounded-lg px-2 py-1.5 text-left text-xs transition ${
                            glutenFreeAllProducts
                              ? "bg-primary text-primary-foreground"
                              : "border border-border bg-background"
                          }`}
                        >
                          <span className="block font-semibold">Everything is gluten-free</span>
                          <span className={glutenFreeAllProducts ? "text-primary-foreground/80" : "text-muted-foreground"}>
                            Badge every listing automatically
                          </span>
                        </button>
                        <button
                          type="button"
                          onClick={() => setGlutenFreeAllProducts(false)}
                          className={`rounded-lg px-2 py-1.5 text-left text-xs transition ${
                            !glutenFreeAllProducts
                              ? "bg-primary text-primary-foreground"
                              : "border border-border bg-background"
                          }`}
                        >
                          <span className="block font-semibold">Choose per product</span>
                          <span className={!glutenFreeAllProducts ? "text-primary-foreground/80" : "text-muted-foreground"}>
                            Answer gluten-free on each listing
                          </span>
                        </button>
                      </div>
                    )}
                  </div>
                )}

                <div className="sm:col-span-2">
                  <FloatingField
                    label="Bio"
                    value={form.bio}
                    onChange={(v) => setField("bio", v)}
                    textarea
                    rows={5}
                  />
                </div>

                <div className="space-y-2 sm:col-span-2">
                  <label className="text-sm font-medium">Profile photo</label>
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                    <input
                      ref={profileImageInputRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => {
                        void handleProfileImageSelected(e.target.files?.[0] ?? null);
                        e.currentTarget.value = "";
                      }}
                    />

                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => profileImageInputRef.current?.click()}
                      disabled={isUploadingProfileImage}
                    >
                      {isUploadingProfileImage ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Uploading
                        </>
                      ) : (
                        <>
                          <Upload className="mr-2 h-4 w-4" />
                          Upload profile photo
                        </>
                      )}
                    </Button>

                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => void handleAdjustProfileImage()}
                      disabled={isUploadingProfileImage || !form.profileImageUrl.trim()}
                    >
                      Adjust
                    </Button>

                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setField("profileImageUrl", "")}
                      disabled={isUploadingProfileImage || !form.profileImageUrl.trim()}
                    >
                      Remove
                    </Button>
                  </div>
                  <FloatingField
                    label="Profile photo URL"
                    value={form.profileImageUrl}
                    onChange={(v) => setField("profileImageUrl", v)}
                  />
                  <p className="text-xs text-muted-foreground">
                    Upload an image or paste a URL. Save profile after uploading to publish it.
                  </p>
                </div>

                <div className="space-y-2 sm:col-span-2">
                  <label className="text-sm font-medium">Cover photo</label>
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                    <input
                      ref={coverImageInputRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => {
                        void handleCoverImageSelected(e.target.files?.[0] ?? null);
                        e.currentTarget.value = "";
                      }}
                    />

                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => coverImageInputRef.current?.click()}
                      disabled={isUploadingCoverImage}
                    >
                      {isUploadingCoverImage ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Uploading
                        </>
                      ) : (
                        <>
                          <Upload className="mr-2 h-4 w-4" />
                          Upload cover photo
                        </>
                      )}
                    </Button>

                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setField("coverImageUrl", "")}
                      disabled={isUploadingCoverImage || !form.coverImageUrl.trim()}
                    >
                      Remove
                    </Button>
                  </div>
                  <FloatingField
                    label="Cover photo URL"
                    value={form.coverImageUrl}
                    onChange={(v) => setField("coverImageUrl", v)}
                  />
                  <p className="text-xs text-muted-foreground">
                    Upload an image or paste a URL. Save profile after uploading to publish it.
                  </p>
                </div>

                <div className="space-y-3 sm:col-span-2">
                  <label className="text-sm font-medium">Preview</label>
                  <div className="overflow-hidden rounded-xl border bg-card">
                    <div className="relative h-32 w-full overflow-hidden bg-brand-gradient-soft">
                      {form.coverImageUrl.trim() ? (
                        <img
                          src={form.coverImageUrl.trim()}
                          alt="Cover preview"
                          className="h-full w-full object-cover"
                        />
                      ) : null}
                    </div>

                    <div className="relative px-4 pb-4">
                      <div className="-mt-8 h-16 w-16 overflow-hidden rounded-full border-4 border-background bg-muted shadow">
                        {form.profileImageUrl.trim() ? (
                          <img
                            src={form.profileImageUrl.trim()}
                            alt="Profile preview"
                            className="h-full w-full object-cover"
                          />
                        ) : (
                          <div className="flex h-full w-full items-center justify-center bg-muted text-lg font-semibold text-muted-foreground">
                            {(form.displayName || form.firstName || "U").slice(0, 1).toUpperCase()}
                          </div>
                        )}
                      </div>

                      <div className="mt-3">
                        <div className="text-base font-semibold">
                          {form.displayName.trim() ||
                            [form.firstName.trim(), form.lastName.trim()].filter(Boolean).join(" ") ||
                            "Your public profile"}
                        </div>
                        <div className="mt-1 text-sm text-muted-foreground">
                          {form.bio.trim() || "Your bio preview will appear here."}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="rounded-lg border bg-muted/30 p-3 text-sm">
                <div><span className="font-medium">Email:</span> {data.email || "—"}</div>
              </div>

              <div className="flex flex-wrap items-center justify-end gap-3 border-t pt-4">
                {saveMessage && (
                  <span className="text-sm text-brand-secondary">
                    {saveMessage}
                  </span>
                )}
                {saveError && (
                  <span className="text-sm text-destructive">{saveError}</span>
                )}
                <Button type="button" onClick={handleSave} disabled={isSaving}>
                  {isSaving ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Saving…
                    </>
                  ) : (
                    "Save profile"
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
          )}

          {view === "security" && (
          <Card>
            <CardContent className="space-y-6 pt-6">
                <CardContent className="space-y-6">
                  <div className="space-y-4">
                    <div className="text-sm font-medium">Change email</div>

                    <div className="grid gap-5 sm:grid-cols-3">
                      <FloatingField
                        label="New email"
                        type="email"
                        value={newEmail}
                        onChange={setNewEmail}
                      />

                      <FloatingField
                        label="Confirm new email"
                        type="email"
                        value={confirmEmail}
                        onChange={setConfirmEmail}
                      />

                      <FloatingField
                        label="Current password"
                        type="password"
                        value={emailPassword}
                        onChange={setEmailPassword}
                      />
                    </div>

                    <div className="text-xs text-muted-foreground">
                      Updating your email requires your current password.
                    </div>

                    {emailMessage && (
                      <div className="text-sm text-brand-secondary">
                        {emailMessage}
                      </div>
                    )}

                    {emailError && (
                      <div className="text-sm text-destructive">
                        {emailError}
                      </div>
                    )}

                    <div className="flex justify-end">
                      <Button type="button" onClick={handleEmailChange} disabled={isChangingEmail}>
                        {isChangingEmail ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Updating email
                          </>
                        ) : (
                          "Update email"
                        )}
                      </Button>
                    </div>
                  </div>

                  <div className="border-t pt-6 space-y-4">
                    <div className="text-sm font-medium">Change password</div>

                    <div className="grid gap-5 sm:grid-cols-3">
                      <FloatingField
                        label="Current password"
                        type="password"
                        value={currentPassword}
                        onChange={setCurrentPassword}
                      />

                      <FloatingField
                        label="New password"
                        type="password"
                        value={newPassword}
                        onChange={setNewPassword}
                      />

                      <FloatingField
                        label="Confirm new password"
                        type="password"
                        value={confirmPassword}
                        onChange={setConfirmPassword}
                      />
                    </div>

                    <div className="text-xs text-muted-foreground">
                      Passwords must be at least 8 characters.
                    </div>

                    {passwordMessage && (
                      <div className="text-sm text-brand-secondary">
                        {passwordMessage}
                      </div>
                    )}

                    {passwordError && (
                      <div className="text-sm text-destructive">
                        {passwordError}
                      </div>
                    )}

                    <div className="flex justify-end">
                      <Button type="button" onClick={handlePasswordChange} disabled={isChangingPassword}>
                        {isChangingPassword ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Updating password
                          </>
                        ) : (
                          "Update password"
                        )}
                      </Button>
                    </div>
                  </div>

                  {(appleAvailable || !hideGoogle) && (
                  <div className="border-t pt-6 space-y-4">
                    <div>
                      <div className="text-sm font-medium">Connected accounts</div>
                      <div className="mt-1 text-xs text-muted-foreground">
                        Link one and you can sign in with a single tap next time, with no
                        password to remember.
                      </div>
                    </div>

                    {appleAvailable && (
                      appleLinked ? (
                        <div className="flex items-center gap-2 rounded-lg border bg-muted/30 px-3 py-2.5 text-sm">
                          <Check className="h-4 w-4 shrink-0 text-brand-secondary" />
                          <span>Apple is linked. Use Sign in with Apple next time.</span>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          {linkAppleMessage && (
                            <div className="text-sm text-brand-secondary">{linkAppleMessage}</div>
                          )}
                          {linkAppleError && (
                            <div className="text-sm text-destructive">{linkAppleError}</div>
                          )}
                          <Button
                            type="button"
                            className="w-full justify-center gap-2"
                            onClick={handleLinkApple}
                            disabled={isLinkingApple}
                            data-testid="button-link-apple"
                          >
                            {isLinkingApple ? (
                              <>
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                Linking
                              </>
                            ) : (
                              <>
                                <AppleIcon />
                                Link your Apple account
                              </>
                            )}
                          </Button>
                          <div className="text-xs text-muted-foreground">
                            Uses the Apple ID already on this iPhone. Nothing to type, and you
                            can hide your email if you want to.
                          </div>
                        </div>
                      )
                    )}

                    {!hideGoogle && (
                    user?.linkedProviders?.includes("google") ? (
                      <div className="text-sm text-muted-foreground">
                        Google is linked to this account. You can sign in with Google.
                      </div>
                    ) : (
                      <>
                        <div className="text-xs text-muted-foreground">
                          Link your Google account so you can sign in with Google next time.
                        </div>

                        {linkGoogleMessage && (
                          <div className="text-sm text-brand-secondary">{linkGoogleMessage}</div>
                        )}
                        {linkGoogleError && (
                          <div className="text-sm text-destructive">{linkGoogleError}</div>
                        )}

                        <div className="flex justify-end">
                          <Button
                            type="button"
                            variant="outline"
                            onClick={handleLinkGoogle}
                            disabled={isLinkingGoogle}
                          >
                            {isLinkingGoogle ? (
                              <>
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                Linking
                              </>
                            ) : (
                              "Link Google account"
                            )}
                          </Button>
                        </div>
                      </>
                    )
                    )}
                  </div>
                  )}

                  <div className="border-t pt-6 space-y-4">
                    <div className="text-sm font-medium text-destructive">Delete account</div>

                    {!deleteOpen ? (
                      <>
                        <p className="text-xs text-muted-foreground">
                          Permanently delete your Stoop account. Your profile and personal
                          info are erased and you're signed out everywhere. Past orders stay
                          (anonymized) for the people you bought from or sold to, and for tax
                          records.
                        </p>
                        <div className="flex justify-end">
                          <Button
                            type="button"
                            variant="outline"
                            className="border-destructive/40 text-destructive hover:bg-destructive/10"
                            onClick={() => setDeleteOpen(true)}
                          >
                            Delete account
                          </Button>
                        </div>
                      </>
                    ) : (
                      <div className="space-y-4">
                        {hasPassword && (
                          <div className="space-y-2">
                            <label className="text-sm font-medium">Your password</label>
                            <Input
                              type="password"
                              value={deletePassword}
                              onChange={(e) => setDeletePassword(e.target.value)}
                              placeholder="Current password"
                            />
                          </div>
                        )}

                        <div className="space-y-2">
                          <label className="text-sm font-medium">Type DELETE to confirm</label>
                          <Input
                            value={deleteConfirmText}
                            onChange={(e) => setDeleteConfirmText(e.target.value)}
                            placeholder="DELETE"
                          />
                        </div>

                        <div className="space-y-2">
                          <label className="text-sm font-medium">
                            Before you go, why are you leaving?{" "}
                            <span className="text-muted-foreground">(optional)</span>
                          </label>
                          <div className="space-y-1.5">
                            {DELETE_REASONS.map((r) => (
                              <label key={r} className="flex items-center gap-2 text-sm">
                                <input
                                  type="radio"
                                  name="delete-reason"
                                  value={r}
                                  checked={deleteReason === r}
                                  onChange={() => setDeleteReason(r)}
                                  className="accent-primary"
                                />
                                {r}
                              </label>
                            ))}
                          </div>
                        </div>

                        <div className="space-y-2">
                          <label className="text-sm font-medium">
                            Anything else?{" "}
                            <span className="text-muted-foreground">(optional)</span>
                          </label>
                          <Textarea
                            value={deleteDetail}
                            onChange={(e) => setDeleteDetail(e.target.value)}
                            placeholder="Tell us what would've made you stay"
                            maxLength={1000}
                          />
                        </div>

                        {deleteBlockers.length > 0 && (
                          <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
                            <p className="font-medium">You still have:</p>
                            <ul className="mt-1 list-disc pl-5">
                              {deleteBlockers.map((b, i) => (
                                <li key={i}>{b}</li>
                              ))}
                            </ul>
                            <p className="mt-2 text-xs">
                              Wrap these up and try again.
                            </p>
                          </div>
                        )}

                        {deleteError && deleteBlockers.length === 0 && (
                          <div className="text-sm text-destructive">{deleteError}</div>
                        )}

                        <div className="flex justify-end gap-2">
                          <Button
                            type="button"
                            variant="ghost"
                            onClick={() => {
                              setDeleteOpen(false);
                              setDeleteError(null);
                              setDeleteBlockers([]);
                            }}
                          >
                            Cancel
                          </Button>
                          <Button
                            type="button"
                            variant="destructive"
                            disabled={isDeleting || deleteConfirmText.trim().toUpperCase() !== "DELETE"}
                            onClick={handleDeleteAccount}
                          >
                            {isDeleting ? (
                              <>
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                Deleting
                              </>
                            ) : (
                              "Permanently delete"
                            )}
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
            </CardContent>
          </Card>
          )}

        </div>
      </main>

      {profileCropSrc && (
        <ProfileImageCropper
          src={profileCropSrc}
          busy={isUploadingProfileImage}
          onCancel={handleProfileCropCancel}
          onConfirm={handleProfileCropConfirm}
        />
      )}
    </div>
  );
}
